"use client";

import { useEffect, useMemo } from "react";
import { useTranslations } from "next-intl";
import { useFieldArray, useWatch } from "react-hook-form";
import { ConfirmationButton, FormContainer } from "@/components/form";
import type { CountryCode } from "@/config/country-config";
import { useCountry } from "@/config/country-context";
import { useRuntimeConfig } from "@/config/runtime-config-context";
import { LogEvent } from "@/datadog/events";
import { sendClientLog } from "@/datadog/utils/send-client-log";
import { applyMockFormDefaults } from "@/dev/console/data/mock-form-values";
import { useConstantSelectOptions } from "@/features/customer-information/hooks/use-constant-options";
import {
	DebtList,
	type DebtListItem,
	MandatoryDebtsBanner,
	TopUpBanner,
	VoluntaryUpsellHeader,
} from "@/features/refinance/components/debt-collection";
import { LoanOverview } from "@/features/refinance/components/loan-overview";
import { useRefinanceForm } from "@/features/refinance/hooks/use-refinance-form";
import type { RefinanceForm } from "@/features/refinance/hooks/use-refinance-form";
import { useRefinanceScenario } from "@/features/refinance/hooks/use-refinance-scenario";
import {
	describeBlockedRefinance,
	emptyManualDebt,
	resolveLockedDebts,
	resolveRefinanceAllocation,
	resolveRefinanceContextForCountry,
	seedDebtsFromContext,
	splitLeaBankDebts,
	sumCommittedDebts,
	toRefinancePayloadDebts,
} from "@/features/refinance/utils";
import { isCommittedDebt } from "@/features/refinance/utils/debt-status";
import { useSubmitTask } from "@/hooks";
import { TaskType } from "@/types";
import { RefinanceContext, RefinanceDebt, RefinancePayload } from "../types";

interface Props {
	taskId: string;
	context: RefinanceContext;
}

type DebtFieldArrayItem = RefinanceDebt & { id: string };

const mapRefinanceFormToPayload = ({
	values,
	context,
	country,
}: {
	values: RefinanceForm;
	context: RefinanceContext;
	country: CountryCode;
}): RefinancePayload => {
	const debts = values.debts ?? [];
	const { loanAmount } = resolveRefinanceAllocation({
		offer: context.offer,
		appliedRefinance: context.appliedRefinance,
		collected: sumCommittedDebts(debts),
	});

	return {
		appliedAmount: loanAmount,
		debts: toRefinancePayloadDebts(debts.filter(isCommittedDebt), country),
	};
};

const RefinanceForm = ({ taskId, context }: Props) => {
	const t = useTranslations("pages.RefinancePage");
	const country = useCountry();
	const { mock } = useRuntimeConfig();

	const refinanceContext = useMemo(
		() => resolveRefinanceContextForCountry(context, country),
		[context, country]
	);

	const { isForced, isTopUp, requiredRedemption, existingLoan } =
		useRefinanceScenario(refinanceContext);

	const methods = useRefinanceForm({ context: refinanceContext }).useForm;
	const { control, getValues, handleSubmit, reset, trigger } = methods;

	const { fields, append, remove } = useFieldArray({ control, name: "debts" });
	const watchedDebts = useWatch({ control, name: "debts" }) as
		RefinanceDebt[] | undefined;

	const submit = useSubmitTask<RefinancePayload>({
		taskId,
		processType: context.process,
		taskType: TaskType.Refinance,
	});

	const typeOptions = useConstantSelectOptions("loan-types");
	const bankOptions = useConstantSelectOptions("banks");

	// Lea Bank debts are never the customer's to redeem, so they stay out of
	// the form and are shown locked instead.
	const { leaBank, others } = useMemo(
		() => splitLeaBankDebts(context.debts),
		[context.debts]
	);

	const seededDebts = useMemo(
		() => seedDebtsFromContext(others, { typeOptions, bankOptions, country }),
		[others, typeOptions, bankOptions, country]
	);

	const lockedDebts = useMemo(
		() => resolveLockedDebts({ leaBank, existingLoan, typeOptions, country }),
		[leaBank, existingLoan, typeOptions, country]
	);

	useEffect(() => {
		reset(
			applyMockFormDefaults<RefinanceForm>({ debts: seededDebts }, {}, mock)
		);
		if (!isForced) {
			void trigger();
		}
	}, [isForced, seededDebts, reset, trigger, mock]);

	// Register debts are seeded first and cannot be removed, so index doubles as their key.
	const items: DebtListItem[] = (fields as DebtFieldArrayItem[]).map(
		(field, index) => ({
			id: field.id,
			index,
			removable: !field.fromRegister,
			seed: field.fromRegister ? seededDebts[index] : undefined,
		})
	);

	const hasSuggestions = seededDebts.some((debt) => debt.fromRegister);
	const hasManualDebts = items.some((item) => item.removable);
	const hasCommittedDebts = (watchedDebts ?? []).some(isCommittedDebt);

	const onAdd = () => append(emptyManualDebt());
	const onRemove = (index: number) => remove(index);

	const onSubmit = handleSubmit(
		async (values) => {
			await submit(
				mapRefinanceFormToPayload({
					values,
					context: refinanceContext,
					country,
				})
			);
		},
		(errors) => {
			void sendClientLog({
				level: "info",
				event: LogEvent.RefinanceSubmitBlocked,
				message: "Refinance submit blocked by its debts",
				task: TaskType.Refinance,
				processType: context.process,
				context: describeBlockedRefinance({
					offer: refinanceContext.offer,
					appliedRefinance: refinanceContext.appliedRefinance,
					debts: getValues("debts") ?? [],
					debtErrors: errors.debts,
				}),
			});
		}
	);

	return (
		<FormContainer
			methods={methods}
			getPayloadPreview={(values) =>
				mapRefinanceFormToPayload({
					values,
					context: refinanceContext,
					country,
				})
			}
		>
			{isTopUp ? (
				<TopUpBanner
					existingLoan={existingLoan}
					required={requiredRedemption}
					hasSuggestions={hasSuggestions}
				/>
			) : (
				isForced && (
					<MandatoryDebtsBanner
						mandatory={requiredRedemption}
						hasSuggestions={hasSuggestions}
					/>
				)
			)}
			{!isForced && (
				<VoluntaryUpsellHeader
					hasSuggestions={hasSuggestions}
					hasManualDebts={hasManualDebts}
				/>
			)}

			<DebtList
				items={items}
				lockedDebts={lockedDebts}
				heading={isForced ? t("requiredDebts.heading") : undefined}
				customerAccountNumber={context.customerAccountNumber}
				onAdd={onAdd}
				onRemove={onRemove}
			/>

			<LoanOverview context={refinanceContext} />

			<ConfirmationButton
				label={
					!isForced && !hasCommittedDebts
						? t("voluntary.continueButton")
						: t("button.validText")
				}
				onSubmit={onSubmit}
			/>
		</FormContainer>
	);
};

export default RefinanceForm;
